import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { HeartHandshake, AlertCircle, ArrowRight } from 'lucide-react';
import PublicShell, { PublicShellStatus } from './public/PublicShell';
import { REGISTRATION_STEPS } from './public/registrationSteps';
import { INVITE_STORAGE_KEY, INVITE_DETAILS_KEY, clearStoredInvite } from '../utils/invite';
import { invalidateMyEventsCache } from '../hooks/useMyEvents';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const EventRegistration = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [invite, setInvite] = useState(null); // resolved invite | { invalid: true } | { error: true }
    const [inviteResolving, setInviteResolving] = useState(false);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Signed-in users arriving from the events list have their email prefilled (?email=)
    const [email, setEmail] = useState(() => searchParams.get('email') || '');
    const [emailError, setEmailError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/public/events/${eventNameId}`);
                setEvent(response.data);
            } catch (err) {
                setError('Event not found');
            } finally {
                setLoading(false);
            }
        };

        if (eventNameId) {
            fetchEvent();
        }
    }, [eventNameId]);

    // Nominated-nonprofit invitation link (?invite=CODE): remember it for the rest of the registration flow
    const inviteCode = searchParams.get('invite');
    const resolveInvite = useCallback(async () => {
        if (!inviteCode) return;
        setInviteResolving(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/donor-circle/public/invite/${encodeURIComponent(inviteCode)}`);
            localStorage.setItem(INVITE_STORAGE_KEY, inviteCode);
            localStorage.setItem(INVITE_DETAILS_KEY, JSON.stringify(response.data));
            setInvite(response.data);
        } catch (err) {
            if (err.response?.status === 404) {
                // The server rejected the code: drop it so it cannot leak into a later registration
                clearStoredInvite();
                setInvite({ invalid: true });
            } else {
                // Network/server problem: keep whatever is stored and let the user retry
                setInvite({ error: true });
            }
        } finally {
            setInviteResolving(false);
        }
    }, [inviteCode]);

    useEffect(() => { resolveInvite(); }, [resolveInvite]);

    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        setSubmitError('');
        if (emailError) {
            setEmailError(value && !validateEmail(value) ? 'Please enter a valid email address' : '');
        }
    };

    const handleNext = async (e) => {
        e?.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) {
            setEmailError('Please enter your email address');
            return;
        }
        if (!validateEmail(trimmed)) {
            setEmailError('Please enter a valid email address');
            return;
        }
        if (inviteResolving) return; // wait for the invite details before moving on

        setIsSubmitting(true);
        setSubmitError('');
        try {
            // A new registration only carries an invite if this visit came through a valid invite link;
            // otherwise forget any invite left over from an earlier, unfinished registration
            if (!invite || invite.invalid || invite.error) {
                clearStoredInvite();
            }
            const response = await axios.post(`${API_BASE_URL}/public/event-registrations/`, {
                email: trimmed,
                event_name_id: eventNameId
            });

            localStorage.setItem('eventRegistrationGuid', response.data.EventRegistrationGuid);
            localStorage.setItem('userEmail', trimmed);
            invalidateMyEventsCache();

            navigate(`/event/${eventNameId}/registration/type`);
        } catch (err) {
            if (err.response?.data?.detail === 'Email already registered for this event') {
                setEmailError('This email is already registered for this event.');
            } else {
                setSubmitError('We couldn’t start your registration. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <PublicShellStatus><span className="pshell-spinner" aria-hidden="true" /> Loading event…</PublicShellStatus>;
    }

    if (error || !event) {
        return <PublicShellStatus error>Event not found</PublicShellStatus>;
    }

    const validInvite = invite && !invite.invalid && !invite.error;

    return (
        <PublicShell
            event={event}
            steps={REGISTRATION_STEPS}
            currentStep={0}
            stepsTitle="Registering takes a few minutes"
            eyebrow="Registration"
            title={validInvite ? 'You’ve been nominated' : 'Register for the event'}
            subtitle={validInvite
                ? <>{invite.nominated_by ? `${invite.nominated_by} nominated ` : 'A Donor Circle member nominated '}<strong>{invite.nominee_name}</strong> to join {invite.event_name || 'this event'}. Start with your work email to set up your organization’s profile.</>
                : 'Start with the email address you’d like us to use. We’ll send your confirmation and sign-in codes there.'}
            footer={<>Already registered? <Link to={`/login?event=${encodeURIComponent(eventNameId)}`}>Sign in</Link></>}
        >
            {validInvite && (
                <div className="pshell-alert info">
                    <HeartHandshake size={16} aria-hidden="true" />
                    <div>Your nomination is linked to this registration. Register with your work email so we can match you to <strong>{invite.nominee_name}</strong>.</div>
                </div>
            )}
            {invite?.invalid && (
                <div className="pshell-alert error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <div>This invitation link is no longer valid. You can still register below, or contact <a href="mailto:service@funds4charity.org">service@funds4charity.org</a>.</div>
                </div>
            )}
            {invite?.error && (
                <div className="pshell-alert error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <div>
                        We couldn’t check your invitation right now.{' '}
                        <button type="button" className="pshell-inline-btn" onClick={resolveInvite} disabled={inviteResolving}>
                            {inviteResolving ? 'Checking…' : 'Try again'}
                        </button>
                    </div>
                </div>
            )}
            {submitError && (
                <div className="pshell-alert error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <div>{submitError}</div>
                </div>
            )}

            <form onSubmit={handleNext} noValidate>
                <div className="pshell-field">
                    <label htmlFor="email" className="pshell-label">
                        Work email <span className="req" aria-hidden="true">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={() => { if (email && !validateEmail(email.trim())) setEmailError('Please enter a valid email address'); }}
                        className={`pshell-input ${emailError ? 'error' : ''}`}
                        placeholder="name@organization.org"
                        autoComplete="email"
                        inputMode="email"
                        autoFocus
                        required
                        aria-invalid={!!emailError}
                        aria-describedby={emailError ? 'email-error' : 'email-hint'}
                        disabled={isSubmitting || inviteResolving}
                    />
                    {emailError
                        ? <span id="email-error" className="pshell-error" role="alert">{emailError}</span>
                        : <span id="email-hint" className="pshell-hint">Use the address you check most often — this becomes your sign-in.</span>}
                </div>

                <div className="pshell-actions stack">
                    <button
                        type="submit"
                        className="pshell-btn primary block"
                        disabled={!email.trim() || !!emailError || isSubmitting || inviteResolving}
                    >
                        {isSubmitting ? 'Starting…' : inviteResolving ? 'Checking invitation…' : <>Continue <ArrowRight size={16} aria-hidden="true" /></>}
                    </button>
                </div>
            </form>
        </PublicShell>
    );
};

export default EventRegistration;
